import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Clock, LogOut, Printer, GraduationCap, Trash2, Copy } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { copyToClipboard } from "../lib/clipboard";
import { useAuth } from "../context/AuthContext";
import { ClockToggle } from "../lib/clockFormat";
import { fmtUK, todayISO } from "../lib/utils";
import { TimetableGrid } from "../components/parent/TimetableGrid";
import { SlotEditorModal } from "../components/parent/SlotEditorModal";
import { NoteEditorModal } from "../components/parent/NoteEditorModal";
import TomorrowGlance from "../components/TomorrowGlance";
import CalendarView from "../components/CalendarView";

export default function KidHome() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [entries, setEntries] = useState({});
  const [notes, setNotes] = useState({});
  const [editingSlot, setEditingSlot] = useState(null);
  const [noteDay, setNoteDay] = useState(null);
  const [view, setView] = useState("timetable");
  const [examForm, setExamForm] = useState({ title: "", date: "", start_time: "", notes: "" });
  const [examPast, setExamPast] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/parent/child/bootstrap");
      setData(data);
      setEntries(data.child.timetable || {});
      setNotes(data.child.notes || {});
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return <div className="min-h-screen flex items-center justify-center font-display font-black text-2xl animate-pulse" data-testid="kid-loading">Loading…</div>;

  const child = data.child;
  const exams = data.exams || [];
  const { days, slots } = child.schedule;
  const dayOverrides = child.schedule.day_overrides || {};
  const holidays = child.holidays || [];
  const slotFor = (day, idx) => (dayOverrides[day]?.slots || slots)[idx];

  const persistEntries = async (next, msg) => {
    setEntries(next);
    setEditingSlot(null);
    try {
      await api.put("/parent/child/timetable", { entries: next });
      toast.success(msg);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const saveSlotEntry = (form) => {
    const key = `${editingSlot.day}:${editingSlot.idx}`;
    const next = { ...entries };
    if (form.subject || form.teacher || form.room) next[key] = form;
    else delete next[key];
    persistEntries(next, "Timetable updated");
  };

  const clearSlotEntry = () => {
    const next = { ...entries };
    delete next[`${editingSlot.day}:${editingSlot.idx}`];
    persistEntries(next, "Slot cleared");
  };

  const saveNote = async (text) => {
    const next = { ...notes };
    if (text.trim()) next[noteDay] = text;
    else delete next[noteDay];
    setNotes(next);
    setNoteDay(null);
    try {
      await api.put("/parent/child/notes", { notes: next });
      toast.success("Note saved");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const addExam = async () => {
    if (!examForm.title.trim() || !examForm.date) return toast.error("Add an exam name and date");
    if (examForm.date < todayISO() && !examPast) { setExamPast(true); return; }
    try {
      await api.post("/parent/child/exams", examForm);
      toast.success("Exam added to the calendar");
      setExamForm({ title: "", date: "", start_time: "", notes: "" });
      setExamPast(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const deleteExam = async (id) => {
    try {
      await api.delete(`/parent/child/exams/${id}`);
      toast.success("Exam removed");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="min-h-screen" data-testid="kid-home">
      <header className="border-b-2 border-zinc-900 bg-[#7DD3FC] sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-9 h-9 shrink-0 rounded-xl border-2 border-zinc-900 bg-white flex items-center justify-center shadow-[3px_3px_0px_0px_#18181B]">
              <Clock className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <div className="font-display font-black text-lg leading-tight truncate" data-testid="kid-greeting">My timetable</div>
              <div className="text-xs font-bold text-zinc-700 truncate">{user.name}</div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0 no-print">
            <ClockToggle className="nb-btn bg-white px-3 py-2 text-sm font-bold" />
            <button onClick={() => window.print()} data-testid="kid-print-btn" className="nb-btn bg-white px-3 py-2 text-sm">
              <Printer className="w-4 h-4" /> <span className="hidden sm:inline">Print</span>
            </button>
            <button onClick={() => { logout(); navigate("/kid/auth"); }} data-testid="kid-logout-btn" className="nb-btn bg-white px-3 py-2 text-sm">
              <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Log out</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="hidden print:block font-display font-black text-xl mb-3">{child.name}'s timetable</div>

        {(child.messages || []).length > 0 && (
          <div className="nb-card p-4 mb-6 bg-[#FDE68A] no-print" data-testid="kid-messages">
            <div className="font-display font-bold text-sm mb-2">Notes from home</div>
            {(child.messages || []).slice().reverse().map((m) => (
              <p key={m.id} className="text-sm font-semibold py-1 border-b border-zinc-900/10 last:border-b-0" data-testid={`kid-message-${m.id}`}>{m.text}</p>
            ))}
          </div>
        )}

        {child.share_enabled && child.share_token && (
          <div className="nb-card p-4 mb-6 flex items-center justify-between gap-3 flex-wrap no-print" data-testid="kid-share-card">
            <div className="text-sm font-bold">Share link — save this timetable to a phone home screen</div>
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <input readOnly className="nb-input flex-1 text-xs min-w-0" data-testid="kid-share-link" value={`${window.location.origin}/k/${child.share_token}`} />
              <button onClick={async () => { const ok = await copyToClipboard(`${window.location.origin}/k/${child.share_token}`); ok ? toast.success("Link copied") : toast.error("Couldn't copy"); }}
                data-testid="kid-share-copy" className="nb-btn bg-white px-3 py-2 text-xs shrink-0"><Copy className="w-3.5 h-3.5" /> Copy</button>
            </div>
          </div>
        )}

        <div className="flex gap-2 mb-6 no-print" data-testid="kid-view-tabs">
          {[["timetable", "Timetable"], ["calendar", "Calendar & exams"]].map(([v, label]) => (
            <button key={v} onClick={() => setView(v)} data-testid={`kid-view-${v}`}
              className={`nb-btn px-5 py-2 text-sm ${view === v ? "bg-[#7DD3FC]" : "bg-white text-zinc-600"}`}>
              {label}
            </button>
          ))}
        </div>

        {view === "timetable" && (
          <>
            <TomorrowGlance schedule={child.schedule} entries={entries} notes={notes} exams={exams}
              resolveEntry={(e) => ({ title: e.subject || "Class", sub: [e.teacher, e.room].filter(Boolean).join(" · ") })} />
            <TimetableGrid days={days} slots={slots} dayOverrides={dayOverrides} entries={entries} notes={notes}
              subscribed={true} onSlot={(d, i) => setEditingSlot({ day: d, idx: i })} onNote={(d) => setNoteDay(d)} />
          </>
        )}

        {view === "calendar" && (
          <div className="grid lg:grid-cols-5 gap-6 items-start" data-testid="kid-calendar-view">
            <div className="lg:col-span-3">
              <CalendarView holidays={holidays} exams={exams} weeks={child.schedule.weeks || 1} />
            </div>
            <div className="lg:col-span-2 space-y-4">
              <div className="nb-card p-5" data-testid="kid-exams-card">
                <h2 className="font-display font-bold text-lg flex items-center gap-2"><GraduationCap className="w-5 h-5" /> My exams</h2>
                <div className="mt-3 space-y-2" data-testid="kid-exams-list">
                  {exams.map((e) => (
                    <div key={e.id} className="rounded-xl border-2 border-zinc-900 p-2.5 bg-[#FDFBF7] flex items-start justify-between gap-2" data-testid={`kid-exam-${e.id}`}>
                      <div className="min-w-0">
                        <div className="font-bold text-sm">{e.title}</div>
                        <div className="text-[11px] text-zinc-500 font-semibold">
                          {new Date(e.date + "T00:00:00").toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}{e.start_time ? ` · ${e.start_time}` : ""}
                        </div>
                      </div>
                      <button onClick={() => deleteExam(e.id)} data-testid={`kid-exam-delete-${e.id}`}
                        className="p-1.5 shrink-0 rounded-lg border-2 border-zinc-900 bg-red-100 hover:bg-red-200 transition-colors duration-150">
                        <Trash2 className="w-3.5 h-3.5 text-red-700" />
                      </button>
                    </div>
                  ))}
                  {exams.length === 0 && <p className="text-sm text-zinc-400 font-semibold" data-testid="kid-exams-empty">No exams yet — add your first one below.</p>}
                </div>
                <div className="mt-3 pt-3 border-t-2 border-zinc-100 space-y-2">
                  <input className="nb-input" placeholder="Exam name, e.g. Maths prelim" data-testid="kid-exam-title"
                    value={examForm.title} onChange={(e) => setExamForm({ ...examForm, title: e.target.value })} maxLength={100} />
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <input type="date" className="nb-input" data-testid="kid-exam-date"
                        value={examForm.date} onChange={(e) => { setExamForm({ ...examForm, date: e.target.value }); setExamPast(false); }} />
                      {examForm.date && <p className="text-[11px] text-zinc-400 font-semibold mt-1">{fmtUK(examForm.date)}</p>}
                    </div>
                    <input type="time" className="nb-input" data-testid="kid-exam-time"
                      value={examForm.start_time} onChange={(e) => setExamForm({ ...examForm, start_time: e.target.value })} />
                  </div>
                  {examPast && (
                    <p className="text-xs font-bold text-amber-800 bg-amber-100 border-2 border-amber-300 rounded-lg p-2" data-testid="kid-exam-past-warning">
                      That date is in the past — press "Add anyway" to save it.
                    </p>
                  )}
                  <button onClick={addExam} data-testid="kid-exam-add" className="nb-btn w-full bg-[#C7D2FE] py-2 text-sm">
                    <GraduationCap className="w-4 h-4" /> {examPast ? "Add anyway" : "Add exam"}
                  </button>
                </div>
              </div>

              <div className="nb-card p-5" data-testid="kid-holidays-card">
                <h2 className="font-display font-bold text-lg">School holidays</h2>
                <div className="mt-3 space-y-2" data-testid="kid-holidays-list">
                  {holidays.map((h) => (
                    <div key={h.id} className="rounded-xl border-2 border-zinc-900 p-2.5 bg-[#FDFBF7]" data-testid={`kid-holiday-${h.id}`}>
                      <div className="font-bold text-sm">{h.name}</div>
                      <div className="text-[11px] text-zinc-500 font-semibold">
                        {new Date(h.start + "T00:00:00").toLocaleDateString("en-GB", { day: "numeric", month: "short" })} – {new Date(h.end + "T00:00:00").toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                      </div>
                    </div>
                  ))}
                  {holidays.length === 0 && <p className="text-sm text-zinc-400 font-semibold">No holidays added yet — ask your parent.</p>}
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {editingSlot && (
        <SlotEditorModal key={`${editingSlot.day}:${editingSlot.idx}`} day={editingSlot.day}
          slot={slotFor(editingSlot.day, editingSlot.idx)} entry={entries[`${editingSlot.day}:${editingSlot.idx}`]}
          onSave={saveSlotEntry} onClear={clearSlotEntry} onClose={() => setEditingSlot(null)} />
      )}

      {noteDay && (
        <NoteEditorModal key={noteDay} noteDay={noteDay} initialText={notes[noteDay] || ""}
          onSave={saveNote} onClose={() => setNoteDay(null)} />
      )}
    </div>
  );
}
